import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Send, Loader2, Sparkles, X } from 'lucide-react';

/**
 * Chat Input Component
 * 
 * Message input with send button, example chips, and auto-suggest
 */
const ChatInput = ({
  onSend,
  isLoading = false,
  placeholder = "Describe your project...",
  examples = [],
  showExamples = true,
  size = 'lg',
  className = '',
}) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(-1);
  const inputRef = useRef(null);

  // Auto-suggest data based on common patterns
  const autoSuggestData = useMemo(() => [
    { trigger: 'concrete', suggestions: ['concrete patio 10x12 feet', 'concrete slab 20x24 feet 4 inches thick', 'concrete walkway 3 feet wide, 20 feet long', 'concrete driveway 12x40 feet'] },
    { trigger: 'drywall', suggestions: ['drywall for 12x14 room with 8ft ceilings', 'drywall for basement 20x30', 'drywall for garage 24x24', 'drywall for master bedroom 15x18'] },
    { trigger: 'paint', suggestions: ['paint living room 15x20', 'paint 3 bedrooms 12x12 each', 'paint entire house interior 2000 sqft', 'paint exterior trim'] },
    { trigger: 'floor', suggestions: ['flooring for 12x15 living room', 'hardwood floor 800 sqft', 'laminate flooring for 3 rooms', 'tile floor bathroom 8x10'] },
    { trigger: 'lumber', suggestions: ['lumber for 16x20 deck', 'framing lumber for 12x14 shed', '2x4 studs for 40 foot wall', 'lumber for 8 foot fence'] },
    { trigger: 'roof', suggestions: ['roofing for 30x40 house', 'shingles for 1500 sqft roof', 'roofing 6/12 pitch 25 squares', 'metal roofing for barn 40x60'] },
    { trigger: 'deck', suggestions: ['deck 12x16 with railings', 'composite deck 20x20', 'deck with stairs 16x24', 'ground level deck 10x12'] },
    { trigger: 'shed', suggestions: ['shed 10x12 materials', 'storage shed 8x10', 'garden shed with windows 12x16'] },
    { trigger: 'fence', suggestions: ['privacy fence 100 linear feet', 'wood fence 6 feet tall 200 ft', 'picket fence front yard'] },
  ], []);

  // Update suggestions based on input
  useEffect(() => {
    if (input.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const lowerInput = input.toLowerCase();
    let matchedSuggestions = [];

    for (const item of autoSuggestData) {
      if (lowerInput.includes(item.trigger) || item.trigger.includes(lowerInput)) {
        matchedSuggestions = [
          ...matchedSuggestions,
          ...item.suggestions.filter(s => 
            s.toLowerCase().includes(lowerInput) && s.toLowerCase() !== lowerInput
          )
        ];
      }
    }

    // Limit to 4 suggestions
    setSuggestions(matchedSuggestions.slice(0, 4));
    setShowSuggestions(matchedSuggestions.length > 0);
    setSelectedSuggestion(-1);
  }, [input, autoSuggestData]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (input.trim() && !isLoading) {
      onSend(input.trim());
      setInput('');
      setShowSuggestions(false);
    }
  };

  const handleExampleClick = (example) => {
    setInput(example);
    inputRef.current?.focus();
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestion(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestion(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
      } else if (e.key === 'Enter' && selectedSuggestion >= 0) {
        e.preventDefault();
        handleSuggestionClick(suggestions[selectedSuggestion]);
        return;
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    }

    if (e.key === 'Enter' && !e.shiftKey && selectedSuggestion < 0) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const defaultExamples = [
    '10x12 patio, 4 inches thick',
    'Drywall for 12x14 room',
    'Paint 3 bedrooms',
    'Deck 16x20 with stairs',
  ];

  const displayExamples = examples.length > 0 ? examples : defaultExamples;

  const sizes = {
    md: {
      input: 'px-3 py-2.5 text-base md:px-4 md:py-3',
      button: 'px-4 py-2.5 md:px-4 md:py-3',
      container: 'p-3 md:p-4',
    },
    lg: {
      input: 'px-4 py-3 text-base md:px-6 md:py-4 md:text-lg',
      button: 'px-5 py-3 md:px-6 md:py-4',
      container: 'p-4 md:p-6',
    },
  };

  const sizeStyles = sizes[size] || sizes.lg;


  return (
    <div className={`bg-white rounded-t-2xl md:rounded-2xl shadow-xl md:shadow-lg border-t md:border border-gray-200 ${sizeStyles.container} ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              id="chat-input"
              name="chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={(e) => {
                if (suggestions.length > 0) setShowSuggestions(true);
                // Scroll into view on mobile when focused
                if (window.innerWidth < 768) {
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 300);
                }
              }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder={placeholder}
              disabled={isLoading}
              inputMode="text"
              autoComplete="off"
              className={`
                w-full border-2 border-gray-200 rounded-xl
                focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100
                disabled:bg-gray-50 disabled:cursor-not-allowed
                transition-all
                ${sizeStyles.input}
              `}
            />
            {input && !isLoading && (
              <button
                type="button"
                onClick={() => setInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            
            {/* Auto-suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden animate-fade-in">
                <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Suggestions
                </div>
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className={`
                      w-full px-4 py-3 text-left text-sm hover:bg-blue-50 transition-colors
                      ${selectedSuggestion === idx ? 'bg-blue-50' : ''}
                    `}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Calculate Button with tooltip */}
          <div className="relative group">
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              className={`
                rounded-xl transition-all flex items-center justify-center gap-2 font-semibold min-h-[44px] min-w-[44px]
                focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2
                ${isLoading 
                  ? 'bg-gradient-to-r from-brand-600 to-accent-600 text-white cursor-wait shadow-md'
                  : input.trim()
                    ? 'bg-gradient-to-r from-brand-600 to-accent-600 text-white hover:from-brand-700 hover:to-accent-700 shadow-md hover:shadow-xl hover:shadow-brand-500/25 focus-visible:ring-brand-600/30'
                    : 'bg-brand-500 text-white opacity-50 cursor-not-allowed'
                }
                ${sizeStyles.button}
              `}
              aria-label={isLoading ? 'Calculating...' : 'Calculate materials'}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                  <span className="hidden sm:inline">Calculating...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" aria-hidden="true" />
                  <span className="hidden sm:inline">Calculate</span>
                </>
              )}
            </button>
            
            {/* Tooltip for disabled state */}
            {!input.trim() && !isLoading && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-lg" role="tooltip">
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                Describe your project to calculate
              </div>
            )}
          </div>
        </div>
      </form>

      {showExamples && displayExamples.length > 0 && (
        <>
          {/* Desktop: Show examples normally */}
          <div className="hidden md:block mt-4">
            <div className="text-sm text-gray-500 mb-2 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Try asking:
            </div>
            <div className="flex flex-wrap gap-2">
              {displayExamples.map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => handleExampleClick(example)}
                  disabled={isLoading}
                  className="px-3 py-1.5 bg-gray-50 hover:bg-brand-50 border border-gray-200 hover:border-brand-300 rounded-full text-sm text-gray-700 hover:text-brand-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed group focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
                  aria-label={`Use example: ${example}`}
                >
                  <span className="opacity-70 group-hover:opacity-100">"</span>
                  {example}
                  <span className="opacity-70 group-hover:opacity-100">"</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Mobile: Horizontal scrollable examples */}
          <div className="md:hidden mt-3 -mx-4 px-4">
            <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Try:
            </div>
            <div className="overflow-x-auto pb-2 no-scrollbar">
              <div className="flex gap-2 min-w-max">
                {displayExamples.map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleExampleClick(example)}
                    disabled={isLoading}
                    className="px-3 py-1.5 bg-gray-50 hover:bg-brand-50 border border-gray-200 hover:border-brand-300 rounded-full text-xs text-gray-700 hover:text-brand-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
                    aria-label={`Use example: ${example}`}
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatInput;
